import { useParams } from "react-router-dom";

import { auditStamp, tierHistoryOf, type McpTierHistory } from "../../api/mcp";
import { LabeebHistory } from "../../components/labeeb/LabeebHistory";
import { Breadcrumbs } from "../../components/Layout";
import { NotSeenRecently, ResourceView } from "../../components/states";
import { Badge, Empty, Page, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { ProductSection } from "../../products/PerProduct";

/** Every tier this merchant has been on, newest first. */
export function TenantHistory({ config }: { config: AppConfig }) {
  const { ref = "" } = useParams();
  const path = `/mcp/admin/tenant/${encodeURIComponent(ref)}/history`;
  const { state, reload } = useResource<McpTierHistory>(config, path, [ref]);

  return (
    <Page title={<span className="ref">{ref}</span>} subtitle="Change history.">
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tenants", to: "/tenants" },
          { label: ref, to: `/tenants/${encodeURIComponent(ref)}` },
          { label: "History" },
        ]}
      />

      <div className="flex flex-col gap-4">
      <ProductSection id="mcp" note="Tiers this merchant has been on">
      <ResourceView
        state={state}
        what="history"
        onRetry={reload}
        notFound={<NotSeenRecently reference={ref} />}
      />

      {state.kind === "ready" && tierHistoryOf(state.data).length === 0 && (
        <Empty>No tier has ever been recorded for this merchant.</Empty>
      )}

      {state.kind === "ready" && tierHistoryOf(state.data).length > 0 && (
        <Table
          head={
            <tr>
              <Th>Tier</Th>
              <Th>Set by</Th>
              <Th>When</Th>
            </tr>
          }
        >
          {tierHistoryOf(state.data).map((h, i) => (
            <tr key={`${h.created_at}-${i}`}>
              <Td>
                <Badge>{h.tier}</Badge>
              </Td>
              <Td>
                {/* "signup" means the row was created automatically on first
                    use — not a person, and saying "signup" reads as one. */}
                {h.source === null ? (
                  <span className="text-slate-500 dark:text-slate-400">unknown</span>
                ) : h.source === "signup" ? (
                  <span className="text-slate-500 dark:text-slate-400">automatic — first use</span>
                ) : (
                  <span className="ref text-xs">{h.source.replace(/^admin:/, "")}</span>
                )}
              </Td>
              <Td>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {h.created_at ? auditStamp(h.created_at) : "—"}
                </span>
              </Td>
            </tr>
          ))}
        </Table>
      )}
      </ProductSection>

      <ProductSection id="labeeb" note="Changes made through Labeeb's admin API">
        <LabeebHistory config={config} tenantId={ref} />
      </ProductSection>
      </div>
    </Page>
  );
}
